import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "card-global space-y-6" }
const _hoisted_2 = { class: "flex flex-wrap items-center justify-between gap-4" }
const _hoisted_3 = { class: "min-w-0" }
const _hoisted_4 = { class: "page-title break-words text-gray-800" }
const _hoisted_5 = {
  key: 0,
  class: "text-gray-500"
}
const _hoisted_6 = { class: "text-sm" }
const _hoisted_7 = /*#__PURE__*/_createElementVNode("span", { class: "mr-2 text-gray-500" }, "ID", -1)
const _hoisted_8 = { class: "antd-tabs" }
const _hoisted_9 = ["aria-label"]
const _hoisted_10 = ["id", "aria-selected", "aria-controls", "onClick"]
const _hoisted_11 = ["id", "aria-labelledby"]
const _hoisted_12 = {
  key: 2,
  class: "text-gray-500"
}

import BackButton from "@/components/common/BackButton.vue";
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import StatusLabel from "@/components/common/StatusLabel.vue";
import PublicId from "@/components/common/PublicId.vue";
import DocumentConstructorPayloadView from "@/components/documents/documentConstructor/DocumentConstructorPayloadView.vue";
import { DocumentConstructorActions } from "@/store/modules/documentConstructor/actions";
import { DocumentConstructorGetters } from "@/store/modules/documentConstructor/getters";
import DocumentConstructor from "@/types/documents/documentConstructor/DocumentConstructor";
import Params from "@/types/documents/documentConstructor/Params";
import { t } from "@/i18n";

type DetailSection =
  | "overview"
  | "parameters"
  | "processes"
  | "access"
  | "integration";

interface DetailTab {
  key: DetailSection;
  label: string;
}


export default /*#__PURE__*/_defineComponent({
  setup(__props) {

const route = useRoute();
const store = useStore();

const item = ref<DocumentConstructor | null>(null);
const loading = ref(false);
const activeTab = ref<DetailSection>("overview");

const hasScope = computed(
  () => item.value?.viewScope != null || item.value?.editScope != null
);
const hasEsedo = computed(
  () => Boolean((item.value?.params as Params | undefined)?.esedoConfig?.enabled)
);

const tabs = computed<DetailTab[]>(() => [
  {
    key: "overview",
    label: t("entityApproval.constructor.mainInfo"),
  },
  {
    key: "parameters",
    label: t("docConstructorForm.params"),
  },
  {
    key: "processes",
    label: `${t("docConstructorForm.process")} (${item.value?.process?.length || 0})`,
  },
  ...(hasScope.value
    ? [{ key: "access" as const, label: t("entityScope.title") }]
    : []),
  ...(hasEsedo.value
    ? [{ key: "integration" as const, label: t("esedo.title") }]
    : []),
]);

const load = async (routeId: unknown) => {
  const id = String(routeId);
  if (!Number.isSafeInteger(id) || id <= 0) {
    item.value = null;
    return;
  }

  loading.value = true;
  item.value = null;
  activeTab.value = "overview";
  try {
    await store.dispatch(DocumentConstructorActions.FETCH_ONE, id);
    const loadedItem = store.getters[
      DocumentConstructorGetters.GET_CURRENT
    ] as DocumentConstructor | null;
    item.value = loadedItem?.id === id ? loadedItem : null;
  } finally {
    loading.value = false;
  }
};

watch(
  () => route.params.id,
  (id) => {
    void load(id);
  },
  { immediate: true }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(BackButton, {
      to: { name: 'DocumentConstructorList' },
      class: "mb-4"
    }),
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("h1", _hoisted_4, _toDisplayString(item.value?.name || _unref(t)("docConstructorView.title")), 1)
        ]),
        (item.value?.status)
          ? (_openBlock(), _createBlock(StatusLabel, {
              key: 0,
              label: item.value.status
            }, null, 8, ["label"]))
          : _createCommentVNode("", true)
      ]),
      (loading.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_5, _toDisplayString(_unref(t)("docConstructorView.loading")), 1))
        : (item.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              _createElementVNode("div", _hoisted_6, [
                _hoisted_7,
                _createVNode(PublicId, {
                  value: item.value.id
                }, null, 8, ["value"])
              ]),
              _createElementVNode("div", _hoisted_8, [
                _createElementVNode("nav", {
                  class: "antd-tabs-nav flex-wrap gap-x-8 gap-y-3",
                  role: "tablist",
                  "aria-label": _unref(t)('docConstructorView.title')
                }, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(tabs), (tab) => {
                    return (_openBlock(), _createElementBlock("button", {
                      id: `category-tab-${tab.key}`,
                      key: tab.key,
                      type: "button",
                      role: "tab",
                      class: _normalizeClass(["antd-tab", { 'antd-tab--active': activeTab.value === tab.key }]),
                      "aria-selected": activeTab.value === tab.key,
                      "aria-controls": `category-panel-${tab.key}`,
                      onClick: ($event: any) => (activeTab.value = tab.key)
                    }, _toDisplayString(tab.label), 11, _hoisted_10))
                  }), 128))
                ], 8, _hoisted_9)
              ]),
              _createElementVNode("div", {
                id: `category-panel-${activeTab.value}`,
                role: "tabpanel",
                "aria-labelledby": `category-tab-${activeTab.value}`
              }, [
                _createVNode(DocumentConstructorPayloadView, {
                  payload: item.value,
                  section: activeTab.value,
                  "collapsible-processes": activeTab.value === 'processes',
                  "compact-overview": ""
                }, null, 8, ["payload", "section", "collapsible-processes"])
              ], 8, _hoisted_11)
            ], 64))
          : (_openBlock(), _createElementBlock("p", _hoisted_12, _toDisplayString(_unref(t)("common.noData")), 1))
    ])
  ], 64))
}
}

})